import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import useAuth from "context/useAuth";
import { api_private_post, showAlert } from "script";
import { refreshSession } from "../api";
import "./Blackjack.css";

const CARD_ANIM_DURATION = 700;

/* =========================================================== */
/* Pure helpers                                                */
/* =========================================================== */

// Scores only the visible cards (dealer hole cards arrive masked as {hidden:true}).
function handScore(cards) {
  let total = 0;
  let softAces = 0;
  for (const card of cards) {
    if (!card || card.hidden) continue;
    total += card.value;
    if (card.rank === "A") softAces += 1;
  }
  while (total > 21 && softAces > 0) {
    total -= 10;
    softAces -= 1;
  }
  return {
    total,
    soft: softAces > 0,
    bust: total > 21,
    blackjack: cards.length === 2 && total === 21,
  };
}

function formatTotal(score) {
  if (!score || score.bust) return "bust";
  return score.soft ? `${score.total} soft` : `${score.total}`;
}

function roundRect(ctx, x, y, width, height, radius) {
  const r = Math.min(radius, width / 2, height / 2);
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + width, y, x + width, y + height, r);
  ctx.arcTo(x + width, y + height, x, y + height, r);
  ctx.arcTo(x, y + height, x, y, r);
  ctx.arcTo(x, y, x + width, y, r);
  ctx.closePath();
}

/* =========================================================== */
/* Canvas rendering                                            */
/* =========================================================== */

function drawBackground(ctx, width, height) {
  const gradient = ctx.createLinearGradient(0, 0, 0, height);
  gradient.addColorStop(0, "#0a1a12");
  gradient.addColorStop(1, "#07100b");
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, width, height);

  const glow = ctx.createRadialGradient(
    width * 0.5,
    height * 0.18,
    10,
    width * 0.5,
    height * 0.18,
    Math.max(width, height) * 0.55,
  );
  glow.addColorStop(0, "rgba(255, 221, 127, 0.18)");
  glow.addColorStop(1, "rgba(255, 221, 127, 0)");
  ctx.fillStyle = glow;
  ctx.fillRect(0, 0, width, height);
}

function drawTable(ctx, width, height) {
  const tableW = width * 0.96;
  const tableH = height * 0.92;
  const x = (width - tableW) / 2;
  const y = (height - tableH) / 2;
  const radius = Math.min(64, width * 0.04);

  roundRect(ctx, x, y, tableW, tableH, radius);
  ctx.save();
  ctx.clip();

  const felt = ctx.createLinearGradient(0, y, 0, y + tableH);
  felt.addColorStop(0, "#123827");
  felt.addColorStop(0.55, "#0d271c");
  felt.addColorStop(1, "#081711");
  ctx.fillStyle = felt;
  ctx.fillRect(x, y, tableW, tableH);

  const innerGlow = ctx.createRadialGradient(
    width * 0.5,
    height * 0.44,
    20,
    width * 0.5,
    height * 0.44,
    Math.max(width, height) * 0.48,
  );
  innerGlow.addColorStop(0, "rgba(255,255,255,0.08)");
  innerGlow.addColorStop(0.6, "rgba(255,255,255,0.03)");
  innerGlow.addColorStop(1, "rgba(255,255,255,0)");
  ctx.fillStyle = innerGlow;
  ctx.fillRect(x, y, tableW, tableH);

  ctx.strokeStyle = "rgba(255,255,255,0.09)";
  ctx.lineWidth = 2;
  ctx.stroke();
  ctx.restore();

  ctx.strokeStyle = "rgba(240, 201, 107, 0.32)";
  ctx.lineWidth = 2;
  roundRect(ctx, x + 10, y + 10, tableW - 20, tableH - 20, radius * 0.8);
  ctx.stroke();
}

function drawLabels(ctx, width, height) {
  ctx.save();
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillStyle = "rgba(240, 201, 107, 0.9)";
  ctx.font = `700 ${Math.max(16, Math.round(width * 0.022))}px Georgia`;
  ctx.fillText("BLACKJACK", width * 0.18, height * 0.83);
  ctx.fillText("CASINO TABLE", width * 0.82, height * 0.83);
  ctx.fillStyle = "rgba(255,255,255,0.9)";
  ctx.font = `700 ${Math.max(14, Math.round(width * 0.016))}px Georgia`;
  ctx.fillText("DEALER HAND", width * 0.5, height * 0.19);
  ctx.fillText("PLAYER", width * 0.5, height * 0.62);
  ctx.restore();
}

function drawShoe(ctx, width, height) {
  const boxW = Math.max(120, width * 0.12);
  const boxH = Math.max(70, height * 0.12);
  const x = width * 0.17 - boxW / 2;
  const y = height * 0.2 - boxH / 2;

  ctx.save();
  ctx.shadowColor = "rgba(0,0,0,0.35)";
  ctx.shadowBlur = 18;
  ctx.shadowOffsetY = 10;
  roundRect(ctx, x, y, boxW, boxH, 20);
  const fill = ctx.createLinearGradient(x, y, x, y + boxH);
  fill.addColorStop(0, "#131715");
  fill.addColorStop(1, "#070909");
  ctx.fillStyle = fill;
  ctx.fill();
  ctx.shadowColor = "transparent";
  ctx.strokeStyle = "rgba(255,255,255,0.12)";
  ctx.lineWidth = 2;
  ctx.stroke();
  ctx.fillStyle = "#c9c5b8";
  ctx.font = `600 ${Math.max(12, Math.round(boxH * 0.18))}px Arial`;
  ctx.textAlign = "center";
  ctx.fillText("DEALER SHOE", x + boxW / 2, y + boxH * 0.34);
  ctx.fillStyle = "#f0c96b";
  ctx.font = `700 ${Math.max(18, Math.round(boxH * 0.28))}px Georgia`;
  ctx.restore();
}

function drawCardBack(ctx, x, y, width, height) {
  ctx.save();
  ctx.translate(x + width / 2, y + height / 2);
  const band = ctx.createLinearGradient(
    -width / 2,
    -height / 2,
    width / 2,
    height / 2,
  );
  band.addColorStop(0, "rgba(255,255,255,0.12)");
  band.addColorStop(0.5, "rgba(255,255,255,0.02)");
  band.addColorStop(1, "rgba(255,255,255,0.12)");
  ctx.fillStyle = band;
  ctx.fillRect(-width / 2 + 8, -height / 2 + 8, width - 16, height - 16);
  ctx.strokeStyle = "rgba(255,255,255,0.28)";
  ctx.lineWidth = 1.5;
  ctx.strokeRect(-width / 2 + 8, -height / 2 + 8, width - 16, height - 16);
  ctx.fillStyle = "rgba(255,255,255,0.72)";
  ctx.font = `800 ${Math.max(16, Math.round(width * 0.28))}px Georgia`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText("\u2605", 0, -10);
  ctx.fillStyle = "rgba(255,255,255,0.5)";
  ctx.font = `700 ${Math.max(12, Math.round(width * 0.14))}px Arial`;
  ctx.fillText("BLACKJACK", 0, 18);
  ctx.restore();
}

function renderCardVisual(
  ctx,
  scene,
  x,
  y,
  width,
  height,
  card,
  hidden,
  tilt,
  now,
) {
  const animation = card && card._anim;
  const startTime = animation
    ? animation.startedAt + (animation.delay || 0)
    : 0;
  const progress = animation
    ? Math.max(
        0,
        Math.min(
          1,
          (now - startTime) / (animation.duration || CARD_ANIM_DURATION),
        ),
      )
    : 1;
  const eased = 1 - Math.pow(1 - progress, 3);
  const baseScale = animation ? 0.2 + eased * 0.8 : 1;
  const floatY = animation ? (1 - eased) * -36 : 0;
  const owner = animation ? animation.owner : "player";
  const startX = animation ? scene.width * 0.5 : x;
  const startY = animation
    ? owner === "dealer"
      ? scene.height * 0.18
      : scene.height * 0.82
    : y;
  const animX = animation ? startX + (x - startX) * eased : x;
  const animY = animation ? startY + (y - startY) * eased + floatY : y;
  const revealProgress =
    animation && animation.revealStartedAt
      ? Math.max(
          0,
          Math.min(
            1,
            (now - animation.revealStartedAt) /
              (animation.revealDuration || CARD_ANIM_DURATION),
          ),
        )
      : 1;
  const revealScaleX =
    animation && animation.revealStartedAt
      ? Math.max(0.05, Math.abs(Math.cos(revealProgress * Math.PI)))
      : 1;
  const revealShowingBack =
    animation && animation.revealStartedAt ? revealProgress < 0.5 : hidden;

  ctx.save();
  ctx.globalAlpha = animation ? 0.2 + eased * 0.8 : 1;
  ctx.translate(animX + width / 2, animY + height / 2);
  ctx.rotate(tilt);
  ctx.scale(baseScale * revealScaleX, baseScale);
  ctx.shadowColor = "rgba(0,0,0,0.32)";
  ctx.shadowBlur = 20;
  ctx.shadowOffsetY = 10;

  const cardX = -width / 2;
  const cardY = -height / 2;
  const radius = Math.max(12, width * 0.16);

  roundRect(ctx, cardX, cardY, width, height, radius);
  if (revealShowingBack) {
    const back = ctx.createLinearGradient(
      cardX,
      cardY,
      cardX + width,
      cardY + height,
    );
    back.addColorStop(0, "#21406a");
    back.addColorStop(0.5, "#102747");
    back.addColorStop(1, "#081120");
    ctx.fillStyle = back;
  } else {
    const fill = ctx.createLinearGradient(cardX, cardY, cardX, cardY + height);
    fill.addColorStop(0, "#fcfbf6");
    fill.addColorStop(1, "#ded8c9");
    ctx.fillStyle = fill;
  }
  ctx.fill();

  ctx.shadowColor = "transparent";
  ctx.lineWidth = 2;
  ctx.strokeStyle = revealShowingBack
    ? "rgba(255,255,255,0.22)"
    : "rgba(0,0,0,0.12)";
  ctx.stroke();

  if (revealShowingBack) {
    drawCardBack(ctx, cardX, cardY, width, height);
    ctx.restore();
    return;
  }

  const red = card.color === "red";
  ctx.fillStyle = red ? "#b52732" : "#1a1a1a";
  ctx.font = `700 ${Math.max(18, Math.round(width * 0.28))}px Georgia`;
  ctx.textAlign = "left";
  ctx.textBaseline = "top";
  ctx.fillText(card.rank, cardX + 10, cardY + 8);
  ctx.font = `600 ${Math.max(14, Math.round(width * 0.19))}px Arial`;
  ctx.fillText(card.suit, cardX + 12, cardY + 32);

  ctx.save();
  ctx.translate(cardX + width - 12, cardY + height - 12);
  ctx.rotate(Math.PI);
  ctx.fillStyle = red ? "#b52732" : "#1a1a1a";
  ctx.font = `700 ${Math.max(18, Math.round(width * 0.28))}px Georgia`;
  ctx.textAlign = "left";
  ctx.textBaseline = "top";
  ctx.fillText(card.rank, 0, 0);
  ctx.font = `600 ${Math.max(14, Math.round(width * 0.19))}px Arial`;
  ctx.fillText(card.suit, 2, 24);
  ctx.restore();

  ctx.fillStyle = red ? "#b52732" : "#1d1d1d";
  ctx.font = `800 ${Math.max(24, Math.round(width * 0.36))}px Georgia`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(card.suit, 0, 0);

  ctx.fillStyle = "rgba(255,255,255,0.22)";
  ctx.fillRect(cardX + 4, cardY + 4, width - 8, 10);
  ctx.restore();
}

function drawEmptyArc(ctx, x, y, text) {
  ctx.save();
  ctx.fillStyle = "rgba(255,255,255,0.1)";
  ctx.beginPath();
  ctx.ellipse(x, y, 120, 32, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = "rgba(255,255,255,0.5)";
  ctx.font = "600 14px Arial";
  ctx.textAlign = "center";
  ctx.fillText(text, x, y + 5);
  ctx.restore();
}

function drawDealerRow(ctx, scene, game, x, y, width, now) {
  const hand = game.dealerHand;
  const cards = hand ? hand.cards : [];
  const gap = Math.min(10, width * 0.01);
  const cardWidth = cards.length
    ? Math.min(
        88,
        Math.max(
          48,
          (width - Math.max(0, cards.length - 1) * gap) / cards.length,
        ),
      )
    : 72;
  const cardHeight = cardWidth * 1.34;
  const totalWidth =
    cards.length * cardWidth + Math.max(0, cards.length - 1) * gap;
  const startX = x + width / 2 - totalWidth / 2;
  const score = handScore(cards);
  const labelY = y + 8;
  const cardY = y + 24;
  const labelWidth = Math.min(width, Math.max(170, totalWidth + 28));

  ctx.save();
  ctx.fillStyle = "rgba(6, 13, 9, 0.48)";
  roundRect(ctx, x + (width - labelWidth) / 2, labelY - 12, labelWidth, 28, 14);
  ctx.fill();
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillStyle = "#f0c96b";
  ctx.font = `700 ${Math.max(13, Math.round(cardWidth * 0.18))}px Georgia`;
  ctx.fillText("DEALER", x + width / 2, labelY - 1);
  ctx.fillStyle = "#d7ddcf";
  ctx.font = `600 ${Math.max(11, Math.round(cardWidth * 0.15))}px Arial`;
  const dealerLabel =
    game.dealerHoleRevealed || game.phase === "settled"
      ? `Total ${formatTotal(score)}`
      : `Showing ${formatTotal(handScore(cards.slice(0, 1)))}`;
  ctx.fillText(dealerLabel, x + width / 2, labelY + 11);
  ctx.restore();

  cards.forEach((card, index) => {
    const hidden = Boolean(card && card.hidden);
    const rx = startX + index * (cardWidth + gap);
    const tilt = hidden ? -0.05 : index === 0 ? -0.08 : 0.05;
    renderCardVisual(
      ctx,
      scene,
      rx,
      cardY,
      cardWidth,
      cardHeight,
      card,
      hidden,
      tilt,
      now,
    );
  });

  if (!cards.length) {
    drawEmptyArc(ctx, x + width / 2, y + 38, "No dealer cards yet");
  }
}

function getPlayerHandSlots(width, height, handCount) {
  const slots = [];
  const cardAreaWidth = Math.min(width * 0.25, 220);
  const cardAreaHeight = Math.min(height * 0.18, 126);
  const centerX = width * 0.5;
  const baseY = height * 0.64;

  if (handCount <= 1) {
    slots.push({
      x: centerX - cardAreaWidth / 2,
      y: baseY - 24,
      width: cardAreaWidth,
      height: cardAreaHeight,
    });
    return slots;
  }
  if (handCount === 2) {
    const gap = Math.min(width * 0.16, 170);
    slots.push({
      x: centerX - gap - cardAreaWidth / 2,
      y: baseY - 36,
      width: cardAreaWidth,
      height: cardAreaHeight,
    });
    slots.push({
      x: centerX + gap - cardAreaWidth / 2,
      y: baseY - 36,
      width: cardAreaWidth,
      height: cardAreaHeight,
    });
    return slots;
  }
  if (handCount === 3) {
    const topY = baseY - 72;
    const gap = Math.min(width * 0.18, 180);
    slots.push({
      x: centerX - gap - cardAreaWidth / 2,
      y: topY,
      width: cardAreaWidth,
      height: cardAreaHeight,
    });
    slots.push({
      x: centerX + gap - cardAreaWidth / 2,
      y: topY,
      width: cardAreaWidth,
      height: cardAreaHeight,
    });
    slots.push({
      x: centerX - cardAreaWidth / 2,
      y: baseY - 14,
      width: cardAreaWidth,
      height: cardAreaHeight,
    });
    return slots;
  }
  const topRowY = baseY - 78;
  const bottomRowY = baseY - 18;
  const gap = Math.min(width * 0.16, 170);
  slots.push({
    x: centerX - gap - cardAreaWidth / 2,
    y: topRowY,
    width: cardAreaWidth,
    height: cardAreaHeight,
  });
  slots.push({
    x: centerX + gap - cardAreaWidth / 2,
    y: topRowY,
    width: cardAreaWidth,
    height: cardAreaHeight,
  });
  slots.push({
    x: centerX - gap - cardAreaWidth / 2,
    y: bottomRowY,
    width: cardAreaWidth,
    height: cardAreaHeight,
  });
  slots.push({
    x: centerX + gap - cardAreaWidth / 2,
    y: bottomRowY,
    width: cardAreaWidth,
    height: cardAreaHeight,
  });
  return slots;
}

function drawPlayerHand(
  ctx,
  scene,
  x,
  y,
  width,
  height,
  hand,
  handIndex,
  active,
  now,
) {
  const gap = Math.min(10, width * 0.04);
  const cardWidth = hand.cards.length
    ? Math.min(
        84,
        Math.max(
          46,
          (width - Math.max(0, hand.cards.length - 1) * gap) /
            hand.cards.length,
        ),
      )
    : 70;
  const cardHeight = cardWidth * 1.34;
  const totalWidth =
    hand.cards.length * cardWidth + Math.max(0, hand.cards.length - 1) * gap;
  const startX = x + (width - totalWidth) / 2;
  const score = handScore(hand.cards);
  const labelY = y + 8;
  const cardY = y + 26;
  const labelHeight = 40;

  if (active) {
    ctx.save();
    ctx.fillStyle = "rgba(240, 201, 107, 0.14)";
    roundRect(ctx, x - 12, y - 18, width + 24, height + 32, 26);
    ctx.fill();
    ctx.restore();
  }

  ctx.save();
  ctx.strokeStyle = active
    ? "rgba(240, 201, 107, 0.8)"
    : "rgba(255,255,255,0.08)";
  ctx.lineWidth = active ? 2.5 : 1.5;
  roundRect(ctx, x, y, width, height, 22);
  ctx.stroke();
  ctx.restore();

  ctx.save();
  ctx.fillStyle = "rgba(6, 13, 9, 0.48)";
  roundRect(ctx, x + 6, labelY - 12, width - 12, labelHeight, 14);
  ctx.fill();
  ctx.textAlign = "left";
  ctx.textBaseline = "middle";
  ctx.fillStyle = active ? "#f0c96b" : "#eef1ea";
  ctx.font = `700 ${Math.max(14, Math.round(cardWidth * 0.19))}px Georgia`;
  ctx.fillText(`Hand ${handIndex + 1}`, x + 16, labelY - 1);
  ctx.fillStyle = "#f0c96b";
  ctx.font = `700 ${Math.max(13, Math.round(cardWidth * 0.18))}px Georgia`;
  ctx.fillText(
    score.soft ? `Soft ${score.total}` : `Total ${score.total}`,
    x + 16,
    labelY + 12,
  );
  ctx.fillStyle = "#d7ddcf";
  ctx.font = `600 ${Math.max(11, Math.round(cardWidth * 0.15))}px Arial`;
  const details = [`Bet ${money(hand.bet)}`];
  if (hand.fromSplitAce) details.push("Split aces");
  if (hand.doubled) details.push("Doubled");
  ctx.fillText(details.join(" \u2022 "), x + 16, labelY + 27);
  ctx.restore();

  hand.cards.forEach((card, index) => {
    const rx = startX + index * (cardWidth + gap);
    const tilt = index === 0 ? -0.08 : 0.06;
    renderCardVisual(
      ctx,
      scene,
      rx,
      cardY,
      cardWidth,
      cardHeight,
      card,
      false,
      tilt + index * 0.02,
      now,
    );
  });
}

function drawPayoutAnimation(ctx, scene, now) {
  const animation = scene.payoutAnimation;
  if (!animation) return;
  const elapsed = now - animation.startedAt;
  const progress = Math.max(0, Math.min(1, elapsed / animation.duration));
  if (progress >= 1) {
    scene.payoutAnimation = null;
    return;
  }
  const { width, height } = scene;
  const x = width * 0.23;
  const y = height * 0.43;
  const scale = 0.8 + Math.sin(progress * Math.PI) * 0.12;
  const alpha =
    progress < 0.15
      ? progress / 0.15
      : progress > 0.8
        ? (1 - progress) / 0.2
        : 1;

  ctx.save();
  ctx.globalAlpha = Math.max(0, Math.min(1, alpha));
  ctx.translate(x, y);
  ctx.scale(scale, scale);
  ctx.shadowColor = "rgba(240, 201, 107, 0.45)";
  ctx.shadowBlur = 24;
  ctx.fillStyle = "rgba(10, 20, 14, 0.62)";
  roundRect(ctx, -88, -32, 176, 64, 20);
  ctx.fill();
  ctx.shadowColor = "transparent";
  ctx.fillStyle = "#f0c96b";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.font = `800 ${animation.label === "push" ? 28 : 30}px Georgia`;
  ctx.fillText(animation.label === "push" ? "PUSH" : animation.label, 0, -2);
  ctx.font = "600 11px Arial";
  ctx.fillStyle = "rgba(255,255,255,0.82)";
  ctx.fillText(animation.label === "push" ? "BET RETURNED" : "WINNINGS", 0, 18);
  ctx.restore();
}

function drawScene(ctx, scene, game, now) {
  const { width, height } = scene;
  ctx.clearRect(0, 0, width, height);
  drawBackground(ctx, width, height);
  drawTable(ctx, width, height);
  drawLabels(ctx, width, height);

  if (game) {
    const dealerY = height * 0.24;
    const handWidth = width * 0.68;
    const dealerX = (width - handWidth) / 2;
    drawDealerRow(ctx, scene, game, dealerX, dealerY, handWidth, now);

    const hands = game.playerHands || [];
    const slots = getPlayerHandSlots(width, height, hands.length || 1);
    hands.forEach((hand, index) => {
      const slot = slots[index] || slots[0];
      const active = index === game.activeHandIndex && game.phase === "player";
      drawPlayerHand(
        ctx,
        scene,
        slot.x,
        slot.y,
        slot.width,
        slot.height,
        hand,
        index,
        active,
        now,
      );
    });
  }

  drawShoe(ctx, width, height);
  drawPayoutAnimation(ctx, scene, now);
}

function anyCardAnimating(game, now) {
  if (!game) return false;
  const cards = [];
  if (game.dealerHand?.cards) cards.push(...game.dealerHand.cards);
  (game.playerHands || []).forEach((hand) => cards.push(...hand.cards));
  return cards.some((card) => {
    const anim = card && card._anim;
    if (!anim) return false;
    const moveActive =
      now <
      anim.startedAt +
        (anim.delay || 0) +
        (anim.duration || CARD_ANIM_DURATION);
    const revealActive =
      anim.revealStartedAt &&
      now < anim.revealStartedAt + (anim.revealDuration || CARD_ANIM_DURATION);
    return moveActive || revealActive;
  });
}

/* =========================================================== */
/* React component                                             */
/* =========================================================== */

const CHIPS = [0.5, 1, 2, 5];
const BLACKJACK_SESSION_REFRESH_MS = 4 * 60 * 1000;

function money(amount) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Math.max(0, Number(amount) || 0));
}

function clampBet(amount) {
  const minimum = 0.5;
  const maximum = 5;
  const step = 0.5;
  const rounded = Math.round((Number(amount) || 0) / step) * step;
  return Number(Math.min(maximum, Math.max(minimum, rounded)).toFixed(2));
}

export default function BlackjackPage() {
  const { user, loading, refreshUser } = useAuth();
  const navigate = useNavigate();

  const canvasRef = useRef(null);
  const sceneRef = useRef({
    ctx: null,
    dpr: 1,
    width: 0,
    height: 0,
    animationRaf: 0,
    resizeRaf: 0,
    payoutAnimation: null,
  });
  const gameRef = useRef(null);

  const [game, setGame] = useState(null);
  const [betAmount, setBetAmount] = useState(0.5);
  const [busy, setBusy] = useState(false);
  const sessionRefreshInFlightRef = useRef(false);

  const bankroll =
    user?.balance !== undefined && user?.balance !== null
      ? Number(user.balance)
      : 0;

  const refreshBlackjackSession = useCallback(async () => {
    if (
      !user ||
      document.visibilityState !== "visible" ||
      sessionRefreshInFlightRef.current
    ) {
      return;
    }

    sessionRefreshInFlightRef.current = true;
    try {
      await refreshSession();
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401) {
        navigate("/login?sessionExpired=1");
        return;
      }
      console.warn("Failed to refresh blackjack session cookie", err);
    } finally {
      sessionRefreshInFlightRef.current = false;
    }
  }, [navigate, user]);

  // Access control: only logged-in real-currency accounts.
  useEffect(() => {
    if (loading) return;
    if (!user) {
      navigate("/login");
    } else if (user.account_type !== "real") {
      navigate("/");
    }
  }, [user, loading, navigate]);

  const scheduleFrame = useCallback(() => {
    const scene = sceneRef.current;
    const step = () => {
      scene.animationRaf = 0;
      if (!scene.ctx) return;
      const now = performance.now();
      drawScene(scene.ctx, scene, gameRef.current, now);
      if (
        anyCardAnimating(gameRef.current, now) ||
        (scene.payoutAnimation &&
          now <
            scene.payoutAnimation.startedAt + scene.payoutAnimation.duration)
      ) {
        scene.animationRaf = window.requestAnimationFrame(step);
      }
    };
    if (!scene.animationRaf) {
      scene.animationRaf = window.requestAnimationFrame(step);
    }
  }, []);

  const renderNow = useCallback(() => {
    const scene = sceneRef.current;
    if (!scene.ctx) return;
    drawScene(scene.ctx, scene, gameRef.current, performance.now());
    scheduleFrame();
  }, [scheduleFrame]);

  const resizeCanvas = useCallback(() => {
    const scene = sceneRef.current;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const bounds = canvas.getBoundingClientRect();
    scene.ctx = ctx;
    scene.dpr = Math.max(1, Math.min(window.devicePixelRatio || 1, 2));
    scene.width = Math.max(1, Math.floor(bounds.width));
    scene.height = Math.max(1, Math.floor(bounds.height));
    canvas.width = Math.floor(scene.width * scene.dpr);
    canvas.height = Math.floor(scene.height * scene.dpr);
    ctx.setTransform(scene.dpr, 0, 0, scene.dpr, 0, 0);
    renderNow();
  }, [renderNow]);

  // Merge a fresh server game into the current one, tagging newly-dealt cards
  // (and a revealed hole card) with entrance animations.
  const applyGame = useCallback(
    (next) => {
      const prev = gameRef.current;
      const now = performance.now();

      const tagNew = (prevCards, nextCards, owner) => {
        if (!nextCards) return;
        const prevLen = prevCards ? prevCards.length : 0;
        nextCards.forEach((card, index) => {
          if (!card || card.hidden) return;
          if (index >= prevLen) {
            card._anim = {
              startedAt: now,
              delay: 0,
              duration: CARD_ANIM_DURATION,
              owner,
            };
          }
        });
      };

      tagNew(prev?.dealerHand?.cards, next.dealerHand?.cards, "dealer");
      (next.playerHands || []).forEach((hand, i) => {
        tagNew(prev?.playerHands?.[i]?.cards, hand.cards, "player");
      });

      // Hole card just revealed -> flip animation.
      const prevHole = prev?.dealerHand?.cards?.[1];
      const nextHole = next.dealerHand?.cards?.[1];
      if (prevHole?.hidden && nextHole && !nextHole.hidden) {
        nextHole._anim = {
          ...(nextHole._anim || {
            startedAt: now,
            duration: CARD_ANIM_DURATION,
            owner: "dealer",
          }),
          revealStartedAt: now,
          revealDuration: CARD_ANIM_DURATION,
        };
      }

      // Payout celebration on settlement.
      if (
        next.phase === "settled" &&
        (next.lastWinnings > 0 ||
          (next.lastPayout > 0 && next.lastWinnings === 0))
      ) {
        const scene = sceneRef.current;
        scene.payoutAnimation = {
          label:
            next.lastWinnings > 0 ? `+${money(next.lastWinnings)}` : "push",
          startedAt: now,
          duration: 1800,
        };
      }

      gameRef.current = next;
      setGame(next);
      renderNow();
    },
    [renderNow],
  );

  const postAction = useCallback(
    (endpoint, body = {}) => {
      if (busy) return;
      setBusy(true);
      api_private_post(
        endpoint,
        body,
        (data) => {
          setBusy(false);
          if (!data || !data.success) {
            showAlert(data?.error || "Action failed.", "danger");
            return;
          }
          applyGame(data.game);
          refreshUser();
        },
        () => {
          setBusy(false);
          showAlert("Action failed. Please try again.", "danger");
        },
      );
    },
    [busy, applyGame, refreshUser],
  );

  // Initial mount: size the canvas, load state, wire resize.
  useEffect(() => {
    if (!user || user.account_type !== "real") return;
    const scene = sceneRef.current;
    resizeCanvas();

    void refreshBlackjackSession();
    const refreshTimer = window.setInterval(() => {
      void refreshBlackjackSession();
    }, BLACKJACK_SESSION_REFRESH_MS);

    const handleResize = () => {
      window.cancelAnimationFrame(scene.resizeRaf);
      scene.resizeRaf = window.requestAnimationFrame(resizeCanvas);
    };
    window.addEventListener("resize", handleResize);

    api_private_post("/blackjack/state", {}, (data) => {
      if (data?.success) {
        applyGame(data.game);
      }
    });

    return () => {
      window.removeEventListener("resize", handleResize);
      window.clearInterval(refreshTimer);
      window.cancelAnimationFrame(scene.animationRaf);
      window.cancelAnimationFrame(scene.resizeRaf);
    };
  }, [applyGame, refreshBlackjackSession, resizeCanvas, user]);

  if (loading) return null;
  if (!user || user.account_type !== "real") return null;

  const phase = game?.phase || "betting";
  const bettingPhase = phase === "betting" || phase === "settled";
  const playerPhase = phase === "player";
  const insurancePhase = phase === "insurance";

  const phaseLabel =
    {
      betting: "Betting",
      dealing: "Dealing",
      insurance: "Insurance",
      player: "Player turn",
      dealer: "Dealer turn",
      settled: "Settled",
    }[phase] || "Betting";

  const adjustBet = (delta) => {
    setBetAmount((prev) => clampBet((Number(prev) || 0.5) + delta));
  };

  const logText = game?.log?.length
    ? game.log
        .slice(0, 5)
        .map((entry) => `${entry.title}: ${entry.text}`)
        .join(" \u2022 ")
    : "Round log will appear here.";

  return (
    <section className="pay_method blackjack-page">
      <div className="container-fluid px-3 px-lg-6">
        <div className="bj-stage">
          <header className="bj-hud">
            <div className="bj-title-block">
              <p>Real-currency table</p>
              <h1>Blackjack</h1>
            </div>
            <div className="bj-stat-strip" aria-label="Game status">
              {phase !== "settled" && <output>{phaseLabel}</output>}
              <output>{money(bankroll)}</output>
              <output>Bet {money(game?.currentBet ?? betAmount)}</output>
              <output>Blackjack {game?.blackjackPays || "3:2"}</output>
              <output>Min {money(game?.minBet ?? 0.5)}</output>
              <output>Max {money(game?.maxBet ?? 5)}</output>
            </div>
          </header>

          <canvas
            ref={canvasRef}
            className="bj-canvas"
            aria-label="Blackjack table"
          />

          <section className="bj-info-rail" aria-label="Table information">
            <output>
              {game?.message || "Place a wager and deal the first round."}
            </output>
            <output>{logText}</output>
          </section>

          <section className="bj-control-dock" aria-label="Controls">
            <label className="bj-bet-control">
              <span>Bet</span>
              <input
                type="number"
                min="0.5"
                step="0.5"
                value={betAmount}
                disabled={!bettingPhase || busy}
                onChange={(e) => setBetAmount(e.target.value)}
                onBlur={(e) => setBetAmount(clampBet(e.target.value))}
              />
            </label>

            <div className="bj-chip-row" aria-label="Quick bet buttons">
              {CHIPS.map((chip) => (
                <button
                  key={chip}
                  type="button"
                  className="bj-chip-btn"
                  disabled={!bettingPhase || busy}
                  onClick={() => adjustBet(chip)}
                >
                  +{money(chip)}
                </button>
              ))}
              <button
                type="button"
                className="bj-chip-btn bj-ghost"
                disabled={!bettingPhase || busy}
                onClick={() => setBetAmount(0.5)}
              >
                Clear
              </button>
            </div>

            <div className="bj-action-row">
              {!playerPhase && !insurancePhase && (
                <button
                  type="button"
                  className="bj-primary-btn"
                  disabled={
                    busy ||
                    phase === "dealing" ||
                    phase === "dealer" ||
                    bankroll < clampBet(betAmount)
                  }
                  onClick={() =>
                    postAction("/blackjack/deal", { bet: clampBet(betAmount) })
                  }
                >
                  {phase === "settled" ? "Deal next round" : "Deal"}
                </button>
              )}
              {playerPhase && (
                <>
                  <button
                    type="button"
                    className="bj-action-btn bj-hit"
                    disabled={busy}
                    onClick={() => postAction("/blackjack/hit")}
                  >
                    Hit
                  </button>
                  <button
                    type="button"
                    className="bj-action-btn bj-stand"
                    disabled={busy}
                    onClick={() => postAction("/blackjack/stand")}
                  >
                    Stand
                  </button>
                  <button
                    type="button"
                    className="bj-action-btn bj-double"
                    disabled={busy || !game?.canDouble}
                    onClick={() => postAction("/blackjack/double")}
                  >
                    Double
                  </button>
                  <button
                    type="button"
                    className="bj-action-btn bj-split"
                    disabled={busy || !game?.canSplit}
                    onClick={() => postAction("/blackjack/split")}
                  >
                    Split
                  </button>
                </>
              )}
              {insurancePhase && (
                <>
                  <button
                    type="button"
                    className="bj-action-btn"
                    disabled={busy || bankroll < (game?.currentBet || 0) / 2}
                    onClick={() =>
                      postAction("/blackjack/insurance", { take: true })
                    }
                  >
                    Insurance
                  </button>
                  <button
                    type="button"
                    className="bj-action-btn bj-ghost"
                    disabled={busy}
                    onClick={() =>
                      postAction("/blackjack/insurance", { take: false })
                    }
                  >
                    No insurance
                  </button>
                </>
              )}
            </div>
          </section>
        </div>
      </div>
    </section>
  );
}
